import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router';
import RaceList from './components/raceList/raceList';
import UnderConstruction from '../../common/underConstruction/underConstruction';
import FilterRow from './components/filterRow/filterRow';
import PaginationControls from '../../common/paginationControls/paginationControls';
import { useRaceData } from './hooks/useRaceData';
import { useFilters } from './hooks/useFilters';
import { usePagination } from '../../common/hooks/usePagination';
import './blindKaizoRaces.css';

export default function BlindKaizoRaces(){
	const [searchParams] = useSearchParams();
	const raceType = searchParams.get('raceType') || '';
	const focusRace = searchParams.get('focusRace');
	const focusRaceNumber = useMemo(() => {
		if (!focusRace) {
			return undefined;
		}
		const n = Number.parseInt(focusRace, 10);
		return Number.isNaN(n) ? undefined : n;
	}, [focusRace]);
	
	const { blindKaizoRaces } = useRaceData(raceType);
	const {
		filterCreator,
		filterGameType,
		filterRacer,
		filterOptions,
		filteredRaces,
		setFilter,
	} = useFilters(blindKaizoRaces);
	
	const {
		currentPage,
		currentPageSize,
		maxPage,
		paginatedItems: paginatedRaces,
		changePage,
		changePageSize,
		resetPage,
	} = usePagination(filteredRaces);

	const scrolledToFocusRaceRef = useRef<string | null>(null);
	const appliedFocusPageForRef = useRef<string | null>(null);

	useEffect(() => {
		resetPage();
	}, [raceType, resetPage]);

	/**
	 * When `focusRace` is in the URL, move to the correct list page once per navigation.
	 * Ref prevents re-running when pagination/page size/maxPage updates; deps are only what
	 * should trigger a fresh attempt (race type, focus param, filtered list).
	 */
	useEffect(() => {
		if (raceType !== 'blindKaizo' || !focusRace) {
			appliedFocusPageForRef.current = null;
			return;
		}
		if (appliedFocusPageForRef.current === focusRace) {
			return;
		}
		const focusNum = Number.parseInt(focusRace, 10);
		if (Number.isNaN(focusNum)) {
			return;
		}
		if (filteredRaces.length === 0) {
			return;
		}

		const index = filteredRaces.findIndex((r) => r.number === focusNum);
		if (index < 0) {
			return;
		}

		let targetPage = 1;
		if (currentPageSize !== 'all') {
			targetPage = Math.floor(index / currentPageSize) + 1;
		}
		if (targetPage !== currentPage && targetPage >= 1 && targetPage <= maxPage) {
			changePage(targetPage);
		}
		appliedFocusPageForRef.current = focusRace;
	}, [raceType, focusRace, filteredRaces]);

	useLayoutEffect(() => {
		if (raceType !== 'blindKaizo' || !focusRace) {
			scrolledToFocusRaceRef.current = null;
			return;
		}
		if (scrolledToFocusRaceRef.current === focusRace) {
			return;
		}

		let cancelled = false;
		let rafId = 0;
		let attempts = 0;
		const maxAttempts = 40;

		const tryScroll = () => {
			if (cancelled) {
				return;
			}
			const raceElement = document.getElementById(`race-${focusRace}`);
			if (raceElement) {
				raceElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
				scrolledToFocusRaceRef.current = focusRace;
				return;
			}
			attempts += 1;
			if (attempts < maxAttempts) {
				rafId = requestAnimationFrame(tryScroll);
			}
		};

		tryScroll();
		return () => {
			cancelled = true;
			cancelAnimationFrame(rafId);
		};
	}, [raceType, focusRace, paginatedRaces]);

	const handleFilterChange = (key: 'creator' | 'gameType' | 'racer', value: string | null) => {
		setFilter(key, value, resetPage);
	}

	if(!raceType){
		return <div>Loading...</div>;
	}

	if(raceType === 'mysteryMonday'){
		return <UnderConstruction />;
	}

	if(raceType === 'blindKaizo' && blindKaizoRaces.length === 0){
		return (
			<div>
				<div className="paginationContainer">
					<PaginationControls
						currentPage={currentPage}
						currentPageSize={currentPageSize}
						maxPage={null}
						raceType={raceType}
						onPageChange={changePage}
						onPageSizeChange={changePageSize}
					/>
				</div>
			</div>
		);
	}
	
	return (
		<div>
			<div className="paginationContainer">
				<PaginationControls
					currentPage={currentPage}
					currentPageSize={currentPageSize}
					maxPage={maxPage}
					raceType={raceType}
					onPageChange={changePage}
					onPageSizeChange={changePageSize}
				/>
				{raceType === 'blindKaizo' && (
					<FilterRow
						filterCreator={filterCreator}
						filterGameType={filterGameType}
						filterRacer={filterRacer}
						filterOptions={filterOptions}
						onFilterChange={handleFilterChange}
					/>
				)}
			</div>
			<RaceList
				races={paginatedRaces}
				raceFilePath={raceType}
				focusRaceNumber={raceType === 'blindKaizo' ? focusRaceNumber : undefined}
			/>
		</div>
	);
}